import DeleteIcon from '@material-ui/icons/Delete';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import EditIcon from 'components/Icons/EditIcon';
import ActionButton from 'components/Buttons/ActionButton';
import GenericList from 'components/List/GenericList';
import { EntityColumnType } from 'components/List/TableCellWithConfigurableStyle';
import { useReceptionsWithFilter } from 'hooks';
import { Routes } from 'routes';
import { TableFilterContext } from 'tableFilterContext';
import { useLatestClosureDate } from 'utils/closureHooks';
import { AppContext } from 'context';
import { checkDateUpperTimeInterval, createUTCDateFromDateString } from 'utils/date';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteReception } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { ReceptionFilters, UserRole } from 'types';
import { generateNotifyFromViolations, isSubmissionErrorType } from 'services/errors';
import { ReceptionFilterBar } from './ReceptionFilterBar';

const columns = [
  {
    columnPath: '',
    headerTranslationKey: 'receptions.action',
    type: 'action' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '5%',
    },
  },
  {
    columnPath: 'receptionDatetime',
    headerTranslationKey: 'receptions.receptionDatetime',
    type: 'date' as EntityColumnType,
    style: {
      columnWidth: '18%',
      paddingLeft: '2rem',
    },
  },
  {
    columnPath: 'receptionDatetime',
    headerTranslationKey: 'receptions.receptionTime',
    type: 'hour' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '12%',
    },
  },
  {
    columnPath: 'client.shortName',
    headerTranslationKey: 'receptions.client',
    style: {
      columnWidth: '18%',
    },
  },
  {
    columnPath: 'stream.name',
    headerTranslationKey: 'receptions.stream',
    style: {
      columnWidth: '10%',
    },
  },
  {
    columnPath: 'netWeight',
    headerTranslationKey: 'receptions.netWeight',
    type: 'quantity' as EntityColumnType,
    style: {
      columnWidth: '17%',
    },
  },
  {
    columnPath: 'refused',
    headerTranslationKey: 'receptions.refused',
    type: 'bool' as EntityColumnType,
    style: {
      columnWidth: '25%',
      paddingRight: '3rem',
    },
  },
];
type CharacterizationListProps = {
  filter: ReceptionFilters | null;
  setFilter: (filter: ReceptionFilters | null) => void;
  setExportDisabled: (isExportDisabled: boolean) => void;
};

const ReceptionList: React.FC<CharacterizationListProps> = ({ filter, setFilter, setExportDisabled }) => {
  const { page, itemsPerPage, order, orderBy } = useContext(TableFilterContext);
  const location = useLocation();
  const history = useHistory();

  const [receptionList, total] = useReceptionsWithFilter({ page, itemsPerPage }, { order, orderBy }, filter);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const minStartDate = sortingCenter?.initializationDate ? new Date(sortingCenter.initializationDate) : new Date();

  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));

  const { t } = useTranslation();
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  useEffect(() => {
    if (receptionList && receptionList.length > 0) {
      setExportDisabled(false);
      return;
    }
    setExportDisabled(true);
  }, [receptionList, setExportDisabled]);

  return (
    <>
      <DeleteConfirmationDialog
        title={t('receptionForm.deletion.confirm')}
        isOpen={Boolean(confirmDeleteId)}
        setOpen={() => setConfirmDeleteId(null)}
        confirmDeletion={async () => {
          if (!confirmDeleteId) return;
          setConfirmDeleteId(null);
          try {
            await deleteReception(confirmDeleteId.toString());
            notify({
              message: t('receptionForm.deletion.success'),
              severity: 'success',
            });
          } catch (e: any) {
            if (isSubmissionErrorType(e) && e.status === 412 && e.response.body.violations) {
              generateNotifyFromViolations(e.response.body.violations, t);
            } else {
              notify({
                message: t('errorGeneric'),
                severity: 'error',
              });
            }
          }
        }}
      />
      <div style={{ marginTop: 30 }}>
        <ReceptionFilterBar
          onChange={setFilter}
          minStartDate={minStartDate}
          maxEndDate={new Date()}
          defaultStartDate={minStartDate}
          defaultEndDate={new Date()}
        />
      </div>
      <GenericList
        noHiddenHour
        entities={receptionList}
        columns={columns}
        total={total}
        detailsLink={Routes.Receptions}
        generateAvailableActions={({ id, receptionDatetime }) => {
          const receptionDate = createUTCDateFromDateString(receptionDatetime);
          const disabled = readOnly || checkDateUpperTimeInterval(receptionDate, closureMonth);

          const linkTo = { pathname: `/receptions/${id}/edit`, state: { referer: location.pathname } };
          return [
            {
              title: t('update'),
              onClick: () => history.push(linkTo.pathname, linkTo.state),
              disabled,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={EditIcon}
                  component={Link}
                  data-qa-id={'edit-button'}
                  to={linkTo}
                  disabled={disabled}
                />
              ),
            },
            {
              title: t('delete'),
              onClick: () => setConfirmDeleteId(id),
              disabled,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={DeleteIcon}
                  data-qa-id={'delete-button'}
                  onClick={() => setConfirmDeleteId(id)}
                  disabled={disabled}
                />
              ),
            },
          ];
        }}
      />
    </>
  );
};
export default ReceptionList;
